import React, { useMemo } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import size from 'lodash/size';
import lowerCase from 'lodash/lowerCase';
import upperFirst from 'lodash/upperFirst';
import { useSelector } from 'react-redux';

import { selectRelationshipPriority } from '../../../redux/board/selectors/relationshipPriority';
import { NEW_CHAT, ADD_PROVIDER_REASON_CODE } from '../../../constants/strings';
import { getListLocalization } from '../../../utils/converters/getListLocalization';
import { useSimpleForm } from '../../common/SimpleFormPopup/utils/useSimpleForm';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';
import { convertList } from '../utils/convertExistingUsersList';
import { ASSIGMENT_STATUS } from '../../../constants/props';
import UserInfoBlock from '../../UserInfoBlock';
import SelectCustom from '../../SelectCustom';
import Popup from '../../common/Popup';
import styles from './index.styl';

const { REASON_CODE, RELATIONSHIP_PRIORITY } = PROFILE_FIELDS_TYPES;
const FIELDS = [REASON_CODE, RELATIONSHIP_PRIORITY];

const AddExistingUser = ({
  users,
  onAdd,
  isOpen,
  onClose,
  pending,
  intl: { formatMessage },
}) => {
  const relationship = useSelector(selectRelationshipPriority);

  const adaptName = name => upperFirst(lowerCase(name));

  const relationshipOptions = useMemo(
    () =>
      relationship.map(item => ({
        label: adaptName(item.name),
        value: item.id,
      })),
    [relationship],
  );
  const { handleFormChange, formState, isValid } = useSimpleForm(
    FIELDS,
    FIELDS,
  );
  const { reason, relation } = formState || {};
  const counter = size(users);
  const localizationId =
    counter === 1 ? 'community.roles.member' : 'community.members';

  const handleAdd = id => {
    const data = {
      ...formState,
      assignmentStatus: ASSIGMENT_STATUS.PERMANENT,
      ids: [id],
    };
    onAdd(data);
  };

  const handleClose = () => {
    handleFormChange({ [REASON_CODE]: '', [RELATIONSHIP_PRIORITY]: '' });
    onClose();
  };

  return (
    <Popup
      opened={isOpen}
      onClose={handleClose}
      width="medium"
      title={formatMessage({ id: 'addPatient.dialog.title' })}
      strictClose
    >
      <div>
        <p className={styles.title}>
          {formatMessage({ id: 'addPatient.dialog.subtitle' })}
        </p>
        <div className={styles.selectWrapper}>
          <SelectCustom
            placeholder={formatMessage(NEW_CHAT.selectReasonForAssignment)}
            label={formatMessage(NEW_CHAT.reasonForAssignment)}
            options={getListLocalization(
              ADD_PROVIDER_REASON_CODE.permanent,
              formatMessage,
            )}
            value={reason}
            onChange={({ value }) => handleFormChange({ [REASON_CODE]: value })}
            needUpdateDefaultValue
            className={styles.select}
          />
          <SelectCustom
            placeholder={formatMessage({
              id: 'relationshipPriority.subtitle',
            })}
            label={formatMessage({
              id: 'relationshipPriority.title',
            })}
            options={relationshipOptions}
            value={relation}
            onChange={({ value }) =>
              handleFormChange({ [RELATIONSHIP_PRIORITY]: value })
            }
            needUpdateDefaultValue
            className={styles.select}
          />
        </div>
        {counter && (
          <div className={styles.counter}>{`${counter} ${formatMessage({
            id: localizationId,
          })}`}</div>
        )}
        {users &&
          users.map(user => {
            const { id, avatar, displayableName, dob } = user || {};
            return (
              <UserInfoBlock
                key={id}
                avatarSize={36}
                avatarUrl={avatar.thumbMin}
                avatarText={displayableName}
                textPrimary={displayableName}
                textComplementary={dob}
                buttonColor="primary"
                list={convertList(formatMessage, user)}
                buttonText={formatMessage({ id: 'common.add' })}
                disabled={!isValid || pending}
                onClick={() => handleAdd(id)}
                className="mb2"
              />
            );
          })}
      </div>
    </Popup>
  );
};

AddExistingUser.propTypes = {
  isOpen: PropTypes.bool,
  onAdd: PropTypes.func,
  onClose: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  users: PropTypes.array,
  pending: PropTypes.bool,
};

export default injectIntl(AddExistingUser);



// WEBPACK FOOTER //
// ./src/components/PatientRegistration/AddExistingUser/index.jsx